import { PureComponent } from "react"
import { Mutation } from "react-apollo"

import TextInput from "../text-input/text-input"
import TextArea from "../text-area/text-area"
import SelectDropdown from "../select-dropown/select-dropdown"
import SaveDiscardEdits from "../save-discard-edits/save-discard-edits"

import { industriesData } from "../../data/industries.json"
import { startupStagesData } from "../../data/startup-stages.json"

import { CREATE_IDEA, UPDATE_IDEA } from "./gql"

import "./idea-form.scss"

const buildInitialState = (initial = {}) => ({
  ideaTitle: initial.ideaTitle || "",
  ideaDescription: initial.ideaDescription || "",
  ideaStage: initial.ideaStage || 0,
  ideaIndustry: initial.ideaIndustry || 0,
  ideaTitleValid: true,
  ideaDescriptionValid: true,
  ideaStageValid: true,
  ideaIndustryValid: true,
  errorMessage: "",
  loading: false,
})

class IdeaForm extends PureComponent {
  constructor(props) {
    super(props)
    this.state = buildInitialState(props.initial)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.initial?.id !== this.props.initial?.id) {
      this.setState(buildInitialState(this.props.initial))
    }
  }

  updateValue = ({ forLabel, value }) => {
    this.setState({
      [forLabel]: value,
      [`${forLabel}Valid`]: true,
      errorMessage: "",
    })
  }

  validate = () => {
    const errors = {}
    if (!this.state.ideaTitle || !this.state.ideaTitle.trim()) {
      errors.ideaTitleValid = false
    }
    if (!this.state.ideaDescription || !this.state.ideaDescription.trim()) {
      errors.ideaDescriptionValid = false
    }
    if (!this.state.ideaIndustry) {
      errors.ideaIndustryValid = false
    }
    if (!this.state.ideaStage) {
      errors.ideaStageValid = false
    }
    if (Object.keys(errors).length) {
      this.setState(errors)
      return false
    }
    return true
  }

  handleError = (error) => {
    this.setState({
      errorMessage: error.message.replace("GraphQL error: ", ""),
      loading: false,
    })
  }

  handleSubmit = (mutate) => {
    if (!this.validate()) return
    this.setState({ loading: true, errorMessage: "" }, mutate)
  }

  handleCompleted = (data) => {
    this.setState(buildInitialState(this.props.initial))
    if (this.props.onCompleted) {
      this.props.onCompleted(data)
    }
  }

  handleDiscard = () => {
    this.setState(buildInitialState(this.props.initial))
    if (this.props.onDiscard) this.props.onDiscard()
  }

  render() {
    const {
      ideaTitle,
      ideaDescription,
      ideaIndustry,
      ideaStage,
      ideaTitleValid,
      ideaDescriptionValid,
      ideaIndustryValid,
      ideaStageValid,
      errorMessage,
      loading,
    } = this.state

    const { initial = {}, refetchQueries = [], submitText, discardText } = this.props
    const isEdit = Boolean(initial.id)
    const mutation = isEdit ? UPDATE_IDEA : CREATE_IDEA

    return (
      <Mutation
        mutation={mutation}
        variables={{
          ideaTitle,
          ideaDescription,
          ideaStage,
          ideaIndustry,
          id: initial.id,
        }}
        refetchQueries={refetchQueries}
        awaitRefetchQueries={true}
        onCompleted={this.handleCompleted}
        onError={this.handleError}
      >
        {(submit) => (
          <div className="idea-form">
            <h3 className="idea-form__label">
              Startup idea title / short description
            </h3>
            <TextInput
              forLabel="ideaTitle"
              type="text"
              updateValue={this.updateValue}
              validationErrorMsg="Please enter a title for your idea"
              isValid={ideaTitleValid}
              value={ideaTitle}
              isEditable={true}
            />

            <h3 className="idea-form__label">Elevator pitch</h3>
            <TextArea
              value={ideaDescription}
              forLabel="ideaDescription"
              updateValue={this.updateValue}
              placeholder="Give us an elevator pitch for your idea — highlight what you're after in a co-founder."
              isValid={ideaDescriptionValid}
              validationErrorMsg="Please describe your idea (200 character max)"
              maxLength={200}
            />

            <h3 className="idea-form__label">Industry</h3>
            <SelectDropdown
              options={industriesData}
              label="Which industry / business type best describes your idea?"
              forLabel="ideaIndustry"
              selectedOptionId={ideaIndustry}
              onClick={this.updateValue}
              isValid={ideaIndustryValid}
              validationErrorMsg="Please select an industry"
              isEditable={true}
            />

            <h3 className="idea-form__label">Stage</h3>
            <SelectDropdown
              options={startupStagesData}
              label="Which stage is your startup in?"
              forLabel="ideaStage"
              selectedOptionId={ideaStage}
              onClick={this.updateValue}
              isValid={ideaStageValid}
              validationErrorMsg="Please select a stage"
              isEditable={true}
            />

            <SaveDiscardEdits
              save={() => this.handleSubmit(submit)}
              discard={this.handleDiscard}
              errorMessage={errorMessage}
              loading={loading}
              saveText={submitText || (isEdit ? "Save changes" : "Post idea")}
              discardText={discardText}
            />
          </div>
        )}
      </Mutation>
    )
  }
}

export default IdeaForm
